/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { PhoneAuthFlowForm } from "@packages/sdk";
import { phoneAuthFlowSchema, useRequestOtp } from "@packages/sdk";
import { useForm } from "react-hook-form";

import type { ThemeType } from "../../../../global/emotion";
import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthPhoneInput } from "./AuthPhoneInput.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const disclaimerStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
});
const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  justifyContent: "flex-end",
};
const footerStyle: Interpolation = {
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: { flexDirection: "column" },
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};

export const PhoneAuthFlow = ({
  phone,
  state,
  setState,
  setFlow,
  allowedMethods,
  hideHeader,
  className = "",
}: AuthFlowProps) => {
  const { track } = useAnalytics();
  const t = useTranslations();
  const alert = useAlert();
  const { mutationPost: sendOtp } = useRequestOtp();
  const isDesktop = useIsDesktopViewport();
  const onSubmit = async ({ phone }: PhoneAuthFlowForm) => {
    try {
      const { exists } = await sendOtp.mutateAsync({ phone });
      setState({ ...state, phone, exists });
      setFlow("otp");
    } catch {
      alert.show({
        title: t("general_word_error"),
        description: t("error_invalid_phone_number"),
      });
    }
  };

  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<PhoneAuthFlowForm>(zodResolver(phoneAuthFlowSchema));
  const form = useForm({ resolver });

  const handleUseEmailInstead = () => {
    track({
      event: HallowAnalyticsEvent.TappedUseEmailInstead,
      properties: { screen: "phone_number_input" },
    });
    setFlow("email");
  };

  useViewedScreen({ screen: "phone_number_input" });

  return (
    <AuthForm<PhoneAuthFlowForm>
      form={form}
      defaultValues={{ phone }}
      onSubmit={onSubmit}
      className={className}
      footerCSS={footerStyle}
      footer={(_, disabled) => (
        <>
          <Text size="l" type="detail" css={disclaimerStyle}>
            {t("phone_number_input_disclaimer")}
          </Text>
          <div
            key={"buttons"}
            css={[
              buttonsStyle,
              !allowedMethods.includes("email") && justOneStyle,
            ]}
          >
            {allowedMethods.includes("email") && (
              <Button
                type={"button"}
                isFullWidth={!isDesktop}
                size="m"
                variant="transparentPrimary"
                onClick={handleUseEmailInstead}
              >
                {t("sms_sign_up_email_instead")}
              </Button>
            )}
            <AuthSubmitButton disabled={disabled}>
              {t("onboarding_button_continue")}
            </AuthSubmitButton>
          </div>
        </>
      )}
    >
      {(form) => (
        <>
          {!hideHeader && <EmailHeader onBack={() => setFlow("main")} />}
          <Text type="headline" size="m" as="h2">
            {t("sms_sign_up_phone_number")}
          </Text>
          <FormField
            control={form.control}
            name="phone"
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthPhoneInput
                  required
                  css={fieldsCSS.phone}
                  {...inputProps}
                  {...field}
                />
                <AuthError css={errorsCSS.phone}>
                  {errors?.phone?.message ? t(errors.phone.message) : ""}
                </AuthError>
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};
